import React, { useState, useEffect, useMemo, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  Grid,
  Card,
  Tabs,
  Tab,
  Icon,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
} from "@mui/material";
import { UserContext } from "App";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import AccountingApi from "../../Classes/API/AccountingApi";
import ServicesApi from "../../Classes/API/Services/ServicesApi";
import toast from "react-hot-toast";
import UpgradeBundlePlan from "./UpgradeBundlePlan";
import { SERVICE_STATUS } from "./constants";
import DatePicker from "components/DatePicker/index";
import dayjs from "dayjs";
import { UnlinkedPackageWarning } from "../../components/UnlinkedPackageWarning";

export default function BundleDetails() {
  const { id } = useParams();
  const [tabValue, setTabValue] = useState(0);
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const CurrentUser = useContext(UserContext);

  const [filterName, setFilterName] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [status, setStatus] = useState(1);
  const [paidStatus, setPaidStatus] = useState("All");

  const [amountToPay, setAmountToPay] = useState("");
  const [paymentModal, setPaymentModal] = useState(false);
  const [paymentSource, setPaymentSource] = useState("cash");

  const [walletModal, setWalletModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState("");
  const [topUpMethod, setTopUpMethod] = useState("cash");

  const [refundModal, setRefundModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundMethod, setRefundMethod] = useState("Direct");

  const fetchBundleDetails = async () => {
    try {
      setLoading(true);
      const filters = {
        serviceName: filterName,
        minPrice: minPrice || null,
        maxPrice: maxPrice || null,
        fromDate: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : null,
        toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : null,
        status: status === "All" ? null : status,
        paidStatus: paidStatus,
      };
      const res = await ServicesApi.GetBundleDetails(id, filters);
      if (res && res.status === "Success") {
        setBundle(res.data);
      } else {
        toast.error(res?.message || "Bundle not found");
      }
    } catch (error) {
      toast.error("Failed to load bundle details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchBundleDetails();
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [
    id,
    filterName,
    minPrice,
    maxPrice,
    fromDate,
    toDate,
    status,
    paidStatus,
  ]);

  const handleTopUp = async () => {
    const amount = Number(topUpAmount);
    if (!topUpAmount || amount <= 0)
      return toast.error("Please enter a valid amount");
    setSubmitting(true);
    const loadId = toast.loading("Processing...");
    try {
      const req = {
        userId: bundle.patientUserId,
        creatorId: CurrentUser.id,
        amount: amount,
        title: `Balance Recharge - Bundle #${bundle.id}`,
        description: `Manual recharge to patient Balance.`,
        category: "Income",
        entryType: "UserBalance",
        paymentMethod: topUpMethod,
      };
      const res = await AccountingApi.CreateAccountingEntry(req);
      if (res.status === "Success" || res.success) {
        toast.success("Balance recharged!", { id: loadId });
        setWalletModal(false);
        setTopUpAmount("");
        fetchBundleDetails();
      } else {
        toast.error(res.message || "Failed", { id: loadId });
      }
    } catch (err) {
      toast.error("Network error", { id: loadId });
    } finally {
      setSubmitting(false);
    }
  };

  const remainingToPay = useMemo(() => {
    return (
      bundle?.bundleItems
        ?.filter((i) => !i.isPaid && i.status !== 3)
        .reduce((sum, i) => sum + i.priceAtTime, 0) || 0
    );
  }, [bundle]);

  const isNotLinkedToPackage = !!bundle && !bundle.packageId;

  const handleOpenPaymentModal = () => {
    setAmountToPay(remainingToPay.toString());
    setPaymentSource("cash");
    setPaymentModal(true);
  };

  const handleCollectPayment = async () => {
    const amount = Number(amountToPay);
    if (!amountToPay || amount <= 0) return toast.error("Invalid amount");
    setSubmitting(true);
    const loadId = toast.loading("Processing...");
    try {
      const payload = {
        bundleId: bundle.id,
        amountToPay: amount,
        paymentSource,
        userId: CurrentUser.id,
      };
      const res = await ServicesApi.CollectPayment(payload);
      if (res.status === "Success") {
        toast.success(`Paid ${amount} EGP`, { id: loadId });
        setPaymentModal(false);
        setAmountToPay("");
        fetchBundleDetails();
      } else {
        toast.error(res.message || "Failed", { id: loadId });
      }
    } catch (err) {
      toast.error("Network error", { id: loadId });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteItem = (itemId) => {
    toast(
      (t) => (
        <span>
          <MDTypography variant="button" fontWeight="medium">
            Are you sure you want to <b>delete</b> this item?
          </MDTypography>
          <MDBox display="flex" justifyContent="center" gap={1} mt={2}>
            <MDButton
              variant="gradient"
              color="secondary"
              size="small"
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="error"
              size="small"
              onClick={async () => {
                toast.dismiss(t.id);
                await executeDeletion(itemId);
              }}
            >
              Delete
            </MDButton>
          </MDBox>
        </span>
      ),
      {
        duration: 6000,
        position: "top-center",
        style: { minWidth: "300px", padding: "16px" },
      },
    );
  };

  const executeDeletion = async (itemId) => {
    const loadId = toast.loading("Removing...");
    try {
      const res = await ServicesApi.RemoveBundleItem(itemId);
      if (res.status === "Success") {
        toast.success("Removed Successfully", { id: loadId });
        fetchBundleDetails();
      } else {
        toast.error(res.message || "Failed to remove", { id: loadId });
      }
    } catch (error) {
      toast.error("Network Error", { id: loadId });
    }
  };

  const handleRefund = async () => {
    const amount = Number(refundAmount);
    if (!refundAmount || amount <= 0 || amount > bundle.accountBalance)
      return toast.error("Invalid amount");
    setSubmitting(true);
    const loadId = toast.loading("Processing...");
    try {
      const res = await ServicesApi.RefundBundle({
        bundleId: bundle.id,
        amount: amount,
        paymentMethod:
          topUpMethod === "visa"
            ? "Visa"
            : topUpMethod === "bank"
              ? "Bank Transfer"
              : "Cash",
        refundMethod: refundMethod,
      });
      if (res.status === "Success") {
        toast.success("Refunded", { id: loadId });
        setRefundModal(false);
        fetchBundleDetails();
      } else {
        toast.error(res.message || "Failed", { id: loadId });
      }
    } catch (err) {
      toast.error("Network error", { id: loadId });
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      Header: "Service Date",
      accessor: "serviceDate",
      Cell: ({ value }) => (
        <MDTypography variant="caption" fontWeight="medium" color="text">
          {dayjs(value).format("DD/MM/YY")}
        </MDTypography>
      ),
    },
    {
      Header: "Service Name",
      accessor: "serviceName",
      Cell: ({ value }) => (
        <MDTypography variant="button" fontWeight="bold">
          {value}
        </MDTypography>
      ),
    },
    {
      Header: "Price",
      accessor: "priceAtTime",
      Cell: ({ value }) => (
        <MDTypography variant="button">
          {value.toLocaleString()} EGP
        </MDTypography>
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }) => {
        const statusId = row.original.status;
        const statusInfo = SERVICE_STATUS[statusId] || {
          label: "Unknown",
          color: "dark",
        };
        return (
          <MDBox display="flex" flexDirection="column">
            <MDBadge
              variant="gradient"
              color={statusInfo.color}
              size="xs"
              badgeContent={statusInfo.label}
              container
            />
            {row.original.isPaid && (
              <MDTypography
                variant="xs"
                color="success"
                fontWeight="bold"
                sx={{ mt: 0.5, display: "flex", alignItems: "center" }}
              >
                <Icon fontSize="inherit" sx={{ mr: 0.5 }}>
                  check_circle
                </Icon>{" "}
                Paid
              </MDTypography>
            )}
          </MDBox>
        );
      },
    },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <MDButton
          color="error"
          variant="text"
          onClick={() => handleDeleteItem(row.original.id)}
        >
          <Icon>delete</Icon>
        </MDButton>
      ),
    },
  ];

  if (loading && !bundle) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress color="info" />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <MDBox
          mt={3}
          mb={4}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDButton
            onClick={() => window.history.back()}
            variant="outlined"
            color="dark"
          >
            <Icon>arrow_back</Icon>&nbsp;Back
          </MDButton>
          <MDBox display="flex" gap={2}>
            <MDButton
              variant="outlined"
              color="success"
              startIcon={<Icon>add_card</Icon>}
              onClick={() => setWalletModal(true)}
            >
              Top-up Balance
            </MDButton>
            <MDButton
              variant="outlined"
              color="info"
              startIcon={<Icon>receipt_long</Icon>}
              onClick={handleOpenPaymentModal}
            >
              Collect Payment
            </MDButton>
            <MDButton
              variant="outlined"
              color="error"
              disabled={!bundle || bundle.accountBalance <= 0}
              startIcon={<Icon>account_balance_wallet</Icon>}
              onClick={() => {
                setRefundAmount(bundle.accountBalance.toString());
                setRefundModal(true);
              }}
            >
              Refund
            </MDButton>
          </MDBox>
        </MDBox>

        {isNotLinkedToPackage && (
          <UnlinkedPackageWarning
            variant="warning"
            message="This Service bundle is currently not linked to any package."
          />
        )}

        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={3}>
            <Card sx={{ p: 2, borderTop: "4px solid #2e60f8" }}>
              <MDTypography variant="caption" fontWeight="bold">
                Patient Record
              </MDTypography>
              <MDTypography variant="h6" fontWeight="bold">
                {bundle?.patientName}
              </MDTypography>
              {bundle?.packageName && (
                <MDBox display="flex" alignItems="center">
                  <MDTypography variant="caption" color="info" fontWeight="bold">
                    Linked to: {bundle.packageName}
                  </MDTypography>
                </MDBox>
              )}
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ p: 2, borderTop: "4px solid #4CAF50" }}>
              <MDTypography variant="caption" color="success" fontWeight="bold">
                Total Paid
              </MDTypography>
              <MDTypography variant="h5" fontWeight="bold">
                {(bundle?.totalPaid || 0).toLocaleString()} EGP
              </MDTypography>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ p: 2, borderTop: "4px solid #FF9800" }}>
              <MDTypography variant="caption" color="warning" fontWeight="bold">
                Total Consumed
              </MDTypography>
              <MDTypography variant="h5" fontWeight="bold">
                {(bundle?.totalConsumed || 0).toLocaleString()} EGP
              </MDTypography>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card
              sx={{
                p: 2,
                borderTop: `4px solid ${bundle?.accountBalance < 0 ? "#f44336" : "#4CAF50"}`,
                bgcolor: bundle?.accountBalance < 0 ? "#fff5f5" : "#f0fdf4",
              }}
            >
              <MDTypography
                variant="caption"
                color={bundle?.accountBalance < 0 ? "error" : "success"}
                fontWeight="bold"
              >
                Bundle Balance
              </MDTypography>
              <MDTypography
                variant="h5"
                fontWeight="bold"
                color={bundle?.accountBalance < 0 ? "error" : "success"}
              >
                {(bundle?.accountBalance || 0).toLocaleString()} EGP
              </MDTypography>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ borderRadius: "12px", overflow: "hidden" }}>
          <MDBox px={2} pt={2} bgcolor="grey-100">
            <Tabs
              value={tabValue}
              onChange={(e, v) => setTabValue(v)}
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab
                label="Services Schedule"
                icon={<Icon>calendar_month</Icon>}
                iconPosition="start"
              />
              <Tab
                label="Update Plan"
                icon={<Icon>add_task</Icon>}
                iconPosition="start"
              />
            </Tabs>
          </MDBox>
          <Divider sx={{ m: 0 }} />
          <MDBox pt={1}>
            {tabValue === 0 ? (
              <MDBox p={2}>
                <Grid container spacing={1} mb={3} alignItems="center">
                  <Grid item xs={12} md={2}>
                    <MDInput
                      label="Service"
                      fullWidth
                      value={filterName}
                      onChange={(e) => setFilterName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <MDInput
                      label="Min"
                      type="number"
                      fullWidth
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <MDInput
                      label="Max"
                      type="number"
                      fullWidth
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <DatePicker
                      label="From"
                      selected={fromDate}
                      onChange={(date) => setFromDate(date)}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <DatePicker
                      label="To"
                      selected={toDate}
                      onChange={(date) => setToDate(date)}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <MDInput
                      select
                      label="Status"
                      fullWidth
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      SelectProps={{ sx: { height: 45 } }}
                    >
                      <MenuItem value="All">All Status</MenuItem>
                      <MenuItem value={1}>Scheduled</MenuItem>
                      <MenuItem value={2}>Completed</MenuItem>
                    </MDInput>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <MDInput
                      select
                      label="Payment"
                      fullWidth
                      value={paidStatus}
                      onChange={(e) => setPaidStatus(e.target.value)}
                      SelectProps={{ sx: { height: 45 } }}
                    >
                      <MenuItem value="All">All Paid</MenuItem>
                      <MenuItem value="Paid">Paid Only</MenuItem>
                      <MenuItem value="Unpaid">Unpaid Only</MenuItem>
                    </MDInput>
                  </Grid>
                </Grid>
                <MDBox
                  sx={{
                    maxHeight: "500px",
                    overflowY: "auto",
                    position: "relative",
                  }}
                >
                  {loading && (
                    <MDBox
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1,
                        bgcolor: "rgba(255,255,255,0.5)",
                      }}
                    >
                      <CircularProgress size={30} />
                    </MDBox>
                  )}
                  <DataTable
                    table={{ columns, rows: bundle?.bundleItems || [] }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                </MDBox>
              </MDBox>
            ) : (
              <MDBox p={5}>
                <UpgradeBundlePlan
                  bundleId={id}
                  onSuccess={() => {
                    setTabValue(0);
                    fetchBundleDetails();
                  }}
                />
              </MDBox>
            )}
          </MDBox>
        </Card>

        {/* Dialogs remain identical in logic */}
        <Dialog
          open={paymentModal}
          onClose={() => !submitting && setPaymentModal(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Collect Payment</DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              <MDTypography variant="h6" mb={1}>
                Total Required: {remainingToPay} EGP
              </MDTypography>
              <MDBox
                bgcolor="#f8f9fa"
                p={1.5}
                borderRadius="lg"
                mb={3}
                border="1px solid #e9ecef"
              >
                <MDTypography variant="button" color="text">
                  Patient Balance:
                  <MDTypography
                    component="span"
                    variant="button"
                    fontWeight="bold"
                    color={bundle?.walletBalance > 0 ? "success" : "error"}
                  >
                    {` ${bundle?.walletBalance?.toLocaleString() || 0} EGP`}
                  </MDTypography>
                </MDTypography>
              </MDBox>
              <MDInput
                label="Amount"
                type="number"
                fullWidth
                value={amountToPay}
                onChange={(e) => setAmountToPay(e.target.value)}
                sx={{ mb: 3 }}
                disabled={submitting}
              />
              <RadioGroup
                value={paymentSource}
                onChange={(e) => setPaymentSource(e.target.value)}
              >
                <FormControlLabel
                  value="visa"
                  control={<Radio />}
                  label="Visa"
                  disabled={submitting}
                />
                <FormControlLabel
                  value="cash"
                  control={<Radio />}
                  label="Cash"
                  disabled={submitting}
                />
                <FormControlLabel
                  value="bank"
                  control={<Radio />}
                  label="Bank Transfer"
                  disabled={submitting}
                />
                <FormControlLabel
                  value="Wallet"
                  control={<Radio />}
                  label="From Patient Balance"
                  disabled={
                    submitting ||
                    Number(bundle?.walletBalance || 0) <
                      Number(amountToPay || 0)
                  }
                />
              </RadioGroup>
            </MDBox>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <MDButton
              onClick={() => setPaymentModal(false)}
              color="secondary"
              disabled={submitting}
            >
              Cancel
            </MDButton>
            <MDButton
              onClick={handleCollectPayment}
              variant="gradient"
              color="info"
              disabled={submitting || !amountToPay || Number(amountToPay) <= 0}
            >
              {submitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Confirm Payment"
              )}
            </MDButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={walletModal}
          onClose={() => !submitting && setWalletModal(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Recharge Balance</DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              {isNotLinkedToPackage && (
                <UnlinkedPackageWarning
                  variant="info"
                  message="This Service Bundle is currently not linked to a package. Link it to a package to use the package balance."
                />
              )}
              <MDInput
                label="Amount (EGP)"
                type="number"
                fullWidth
                value={topUpAmount}
                onChange={(e) => setTopUpAmount(e.target.value)}
                disabled={submitting}
              />
              <RadioGroup
                value={topUpMethod}
                onChange={(e) => setTopUpMethod(e.target.value)}
                sx={{ mt: 2 }}
              >
                <FormControlLabel
                  value="visa"
                  control={<Radio />}
                  label="Visa"
                  disabled={submitting}
                />
                <FormControlLabel
                  value="cash"
                  control={<Radio />}
                  label="Cash"
                  disabled={submitting}
                />
                <FormControlLabel
                  value="bank"
                  control={<Radio />}
                  label="Bank Transfer"
                  disabled={submitting}
                />
              </RadioGroup>
            </MDBox>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <MDButton
              onClick={() => setWalletModal(false)}
              color="secondary"
              disabled={submitting}
            >
              Cancel
            </MDButton>
            <MDButton
              onClick={handleTopUp}
              variant="gradient"
              color="success"
              disabled={submitting || !topUpAmount}
            >
              Confirm
            </MDButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={refundModal}
          onClose={() => !submitting && setRefundModal(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Refund Balance</DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              <MDTypography variant="h6" color="error" mb={2}>
                Available: {bundle?.accountBalance?.toLocaleString()} EGP
              </MDTypography>
              <MDInput
                label="Refund Amount"
                type="number"
                fullWidth
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                sx={{ mb: 3 }}
                disabled={submitting}
              />
              <RadioGroup
                value={refundMethod}
                onChange={(e) => setRefundMethod(e.target.value)}
              >
                <FormControlLabel
                  value="Direct"
                  control={<Radio />}
                  label="Direct Cash Out"
                  disabled={submitting}
                />
                <FormControlLabel
                  value="Wallet"
                  control={<Radio />}
                  label="Move to Wallet"
                  disabled={submitting}
                />
              </RadioGroup>
            </MDBox>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <MDButton
              onClick={() => setRefundModal(false)}
              color="secondary"
              disabled={submitting}
            >
              Cancel
            </MDButton>
            <MDButton
              onClick={handleRefund}
              variant="gradient"
              color="error"
              disabled={
                submitting ||
                !refundAmount ||
                Number(refundAmount) <= 0 ||
                Number(refundAmount) > (bundle?.accountBalance || 0)
              }
            >
              {submitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Confirm Refund"
              )}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}
